import { useQuery } from '@tanstack/react-query'
import { useTheme } from '@mui/material/styles'
import { collection, getDocs, query } from 'firebase/firestore'
import { db } from 'config/firebase'
import { LeadStatus } from '@otw/models/leads/lead'
import {
  getStatusLabel,
  getBorderColor,
} from 'views/SuperAdmin/Management/LeadManagement/util/statusHelpers'

export interface CustomLeadColumn {
  id: string
  name: string
  color: string
  position: number
  createdBy: string
}

interface UseLeadColumnsReturn {
  customColumns: CustomLeadColumn[]
  isLoading: boolean
  error: Error | null
  /**
   * Resolve a column id (default lead status or custom column id) to a
   * user-friendly label. Falls back to the default-status label, and finally
   * to the raw id if no match is found.
   */
  getColumnLabel: (status: string | LeadStatus | null | undefined) => string
  /**
   * Resolve a column id to its accent color (hex). Custom columns return
   * their stored color; default statuses fall back to `getBorderColor`.
   */
  getColumnColor: (status: string | LeadStatus | null | undefined) => string
}

const DEFAULT_LEAD_STATUSES = new Set<string>([
  'new',
  'inProgress',
  'waitingFinance',
  'closed',
  'lost',
])

const isDefaultStatus = (status: string): status is LeadStatus =>
  DEFAULT_LEAD_STATUSES.has(status)

export const useLeadColumns = (): UseLeadColumnsReturn => {
  const theme = useTheme()
  const { data, isLoading, error } = useQuery<CustomLeadColumn[]>({
    queryKey: ['leadColumns'],
    queryFn: async () => {
      const snapshot = await getDocs(query(collection(db, 'leadColumns')))
      return snapshot.docs.map((d) => d.data() as CustomLeadColumn)
    },
    staleTime: 5 * 60 * 1000,
  })

  const customColumns = data ?? []

  const getColumnLabel = (status: string | LeadStatus | null | undefined): string => {
    if (status == null) return getStatusLabel(null)
    if (typeof status === 'string' && !isDefaultStatus(status)) {
      const custom = customColumns.find((c) => c.id === status)
      if (custom) return custom.name
    }
    return getStatusLabel(status as LeadStatus)
  }

  const getColumnColor = (status: string | LeadStatus | null | undefined): string => {
    if (status == null) return getBorderColor(null, theme)
    if (typeof status === 'string' && !isDefaultStatus(status)) {
      const custom = customColumns.find((c) => c.id === status)
      if (custom) return custom.color
    }
    return getBorderColor(status as LeadStatus, theme)
  }

  return {
    customColumns,
    isLoading,
    error: (error as Error | null) ?? null,
    getColumnLabel,
    getColumnColor,
  }
}
